/**
 * hooks/useSessionTracker.ts
 *
 * Handles two distinct concerns on each page-load / tab-focus:
 *
 * 1. Session counting (session_started event)
 *    - Fires at most once per UTC calendar day (enforced by a unique partial index
 *      in the DB via log_user_event RPC + ON CONFLICT DO NOTHING).
 *    - Client-side 60-minute gap check acts as a courtesy throttle so the RPC
 *      is not called on every minor focus event; the DB constraint is the final guard.
 *
 * 2. Last-seen timestamp (touch_user_last_seen RPC)
 *    - Called on every mount and on every visibilitychange/focus, regardless of
 *      the 60-minute rule.
 *    - Updates users.last_seen_at, which feeds "Dernière activité" in the admin
 *      analytics table via vw_user_activity_summary.
 *    - This ensures a simple reconnection (without a new session event) still
 *      refreshes "Dernière activité".
 *
 * Only active for non-admin, authenticated users (userId is truthy).
 *
 * Updated May 2026: added touchLastSeen(); session_started is now the only call
 * site (duplicate in AuthContext.loadUserProfile removed to avoid double-fire).
 */

import { useEffect, useRef } from 'react';
import { logEvent } from '../utils/analytics';
import { supabase } from '../lib/supabase';

const SESSION_GAP_MS = 60 * 60 * 1000; // 60 minutes

async function touchLastSeen(): Promise<void> {
  try {
    const { error } = await supabase.rpc('touch_user_last_seen');
    if (error) console.warn('touchLastSeen RPC error:', error.message);
  } catch (err) {
    console.warn('touchLastSeen unexpected error:', err);
  }
}

export function useSessionTracker(userId: string | undefined) {
  const lastActivityRef = useRef<number>(0);

  useEffect(() => {
    if (!userId) return;

    async function onActive() {
      // Always update last_seen_at so "Dernière activité" is current.
      touchLastSeen();

      // Only count a new session if the 60-minute gap has elapsed.
      const now = Date.now();
      if (now - lastActivityRef.current >= SESSION_GAP_MS) {
        lastActivityRef.current = now;
        await logEvent('session_started');
      }
    }

    // Fire immediately on mount (first login / page load while authenticated)
    onActive();

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        onActive();
      }
    }

    function handleFocus() {
      onActive();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId]);
}
